"use client";

import { useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/track";

const SORT_OPTIONS = [
  { labelKey: "featured", value: "featured" },
  { labelKey: "sortNewest", value: "newest" },
  { labelKey: "sortPriceLow", value: "price-asc" },
  { labelKey: "sortPriceHigh", value: "price-desc" },
  { labelKey: "sortPopular", value: "popular" },
  { labelKey: "sortNameAZ", value: "name-asc" },
] as const;

export type SortValue = (typeof SORT_OPTIONS)[number]["value"];

export interface ProductSortProps {
  className?: string;
}

export function ProductSort({ className }: ProductSortProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations("product");
  const currentSort = (searchParams.get("sort") as SortValue) || "featured";

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const params = new URLSearchParams(searchParams.toString());
      const value = e.target.value;

      trackEvent("sort_changed", { sortBy: value });

      if (value === "featured") {
        params.delete("sort");
      } else {
        params.set("sort", value);
      }

      // Reset to page 1 when sort changes
      params.delete("page");

      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  return (
    <div className={cn("relative flex items-center gap-2", className)}>
      <label
        htmlFor="product-sort"
        className="text-xs font-medium uppercase tracking-widest text-brand-muted whitespace-nowrap"
      >
        {t("sortBy")}
      </label>
      <div className="relative">
        <select
          id="product-sort"
          value={currentSort}
          onChange={handleChange}
          className="appearance-none rounded-sm border border-brand-dark/15 bg-white py-2 pe-8 ps-3 font-sans text-sm text-brand-dark shadow-sm transition-all duration-200 hover:border-brand-gold/50 focus:border-brand-gold focus:outline-none focus:ring-1 focus:ring-brand-gold/30"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey as "featured" | "sortNewest" | "sortPriceLow" | "sortPriceHigh" | "sortPopular" | "sortNameAZ")}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute end-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brand-muted" />
      </div>
    </div>
  );
}
